<!-- src/views/org-dashboard/settings/OrgSettingsEvaluate.vue -->
<template>
  <div class="evaluate-settings p-base">
    
    <!-- Header with Save Button -->
    <div class="flex items-left justify-between mb-8">
      <div>
        <h3 class="text-[28px] leading-tight font-semibold text-[#2c3e50] mb-2">Evaluation Metrics</h3>
        <p class="text-[#666] text-base">Configure how talent is assessed across different trajectories.</p>
      </div>
      <vs-button 
        @click="saveConfigurationToServer" 
        color="#f5bb00" 
        type="filled" 
        class="px-8 py-2.5 shadow-md hover:shadow-lg transform active:scale-95 transition-all duration-200"
      >
        <span class="font-bold text-white text-base">Save Changes</span>
      </vs-button>
    </div>

    <!-- Metric Type Switcher (Appraisal, Fitment, etc.) -->
    <vs-row>
      <vs-col class="w-full">
        <div class="my-4 text-white flex justify-center">
          <div class="flex gap-3">
            <vs-button 
              class="hover:bg-primary hover:text-white px-6 rounded-full" 
              v-for="(config, key) in configTypes"
              :key="key" 
              @click="selectedConfigType = key"
              :type="selectedConfigType === key ? 'filled' : 'border'"
              :color="selectedConfigType === key ? 'primary' : 'gray'"
            >
              {{ config.title }}
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>

    <!-- Main Content Area: Categories and Criteria -->
    <vs-row>
      <vs-col class="w-full">
        <div class="space-y-8 mt-8">
          <div v-for="(criteria, categoryName) in getCurrentConfig()" :key="categoryName" class="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
            
            <h3 class="text-sm font-bold mb-6 text-center text-[#2c3e50] uppercase tracking-wider">
              {{ categoryName || 'Special Factors' }}
            </h3>

            <div class="space-y-4">
              <div v-for="(criterion, index) in criteria" :key="index" class="criterion-item border-b border-gray-100 last:border-b-0 py-3">
                <vs-row vs-align="flex-start" vs-type="flex" vs-justify="space-between" class="w-full">
                  
                  <!-- Left Column: Title, Key, Description -->
                  <vs-col vs-w="8">
                    <div class="text-left pr-4">
                      <div class="flex items-baseline gap-2 mb-1">
                        <h4 class="font-medium text-base text-[#2c3e50] uppercase">{{ criterion.title }}</h4>
                        <span class="text-xs text-gray-400">({{ criterion.key }})</span>
                      </div>
                      <p v-if="criterion.desc" class="text-sm text-gray-500 italic">{{ criterion.desc }}</p>
                    </div>
                  </vs-col>

                  <!-- Right Column: Controls -->
                  <vs-col vs-w="4">
                    <div class="flex items-center justify-end gap-2 h-full">
                      
                      <!-- Weight -->
                      <span class="text-sm text-gray-500">Weight:</span>
                      <vs-input 
                        type="number" 
                        v-model="criterion.value" 
                        class="w-20" 
                      />
                      <span class="text-sm">%</span>

                      <!-- Edit Button -->
                      <vx-tooltip text="Edit Criteria" position="top">
                        <vs-button 
                          type="flat" 
                          color="primary"
                          icon-pack="feather" 
                          icon="icon-edit-2"
                          class="w-8 h-8 rounded-lg hover:bg-primary-light ml-2"
                          @click="openEditCriterionModal(categoryName, index)" 
                        />
                      </vx-tooltip>
                      
                      <!-- Delete Button -->
                      <vx-tooltip text="Delete Criteria" position="top">
                        <vs-button 
                          type="flat" 
                          color="danger" 
                          icon="delete"
                          class="w-8 h-8 rounded-lg hover:bg-danger-light"
                          @click="removeCriterion(categoryName, index)" 
                        />
                      </vx-tooltip>

                    </div>
                  </vs-col>
                </vs-row>
              </div>

              <!-- Add Criteria Button (Rounded Pill Style) -->
              <vs-button 
                type="border" 
                color="primary" 
                class="w-full mt-4 rounded-full border-yellow-400 hover:bg-yellow-50" 
                @click="openCriterionModal(categoryName)"
              >
                + Add Criteria
              </vs-button>
            </div>
          </div>
        </div>
      </vs-col>
    </vs-row>

    <!-- Custom Add Category Button -->
    <vs-row class="mt-10 mb-10">
      <vs-col class="w-full flex items-center justify-center">
        <vs-button
            type="border"
            class="custom-category-btn"
            @click="openCategoryModal"
            >
            <i class="material-icons text-[16px]">folder</i>
            <span class="ml-1">+ Add New Category</span>
        </vs-button>
      </vs-col>
    </vs-row>

    <!-- Category Modal -->
    <vs-popup class="popup-card" v-model="showCategoryModal" :active.sync="showCategoryModal" :button-close-hidden="true">
      <div class="p-6 relative">
        <!-- Close (X) Icon -->
        <div class="absolute top-4 right-4 cursor-pointer text-gray-400 hover:text-gray-600 transition-colors" @click="closeCategoryModal">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </div>

        <h3 class="text-xl font-semibold mb-6 text-[#2c3e50]">Add New Category</h3>
        <vs-input v-model="newCategory" label="Category Name" placeholder="Enter category name" class="w-full mb-6" @keyup.enter="addCategory" />
        
        <div class="flex justify-end gap-3">
          <vs-button type="border" color="dark" @click="closeCategoryModal">Cancel</vs-button>
          <vs-button color="primary" @click="addCategory">Add Category</vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Criteria Modal (Edit / Add) -->
    <vs-popup class="popup-card" v-model="showCriteriaModal" :active.sync="showCriteriaModal" :button-close-hidden="true">
      <div class="p-6 relative">
        <!-- Close (X) Icon -->
        <div class="absolute top-4 right-4 cursor-pointer text-gray-400 hover:text-gray-600 transition-colors" @click="closeCriterionModal">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </div>

        <h3 class="text-xl font-semibold mb-6 text-[#2c3e50]">{{ isEditing ? 'Edit Criteria' : 'Add New Criteria' }}</h3>
        
        <vs-input v-model="newCriteria.title" label="Title" placeholder="Enter criteria title" class="w-full mb-4" />
        <vs-input v-model="newCriteria.key" label="Key" placeholder="Enter criteria key (e.g., KP)" class="w-full mb-4" />
        <vs-input v-model="newCriteria.desc" label="Description" placeholder="Enter criteria description" class="w-full mb-6" />

        <div class="flex justify-end gap-3">
          <vs-button type="border" color="dark" @click="closeCriterionModal">Cancel</vs-button>
          <vs-button color="primary" @click="isEditing ? updateCriterion() : addCriterion()">
            {{ isEditing ? 'Save Changes' : 'Add Criteria' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

  </div>
</template>

<script>
export default {
  data() {
    return {
      selectedConfigType: 'appraisal',
      showCriteriaModal: false,
      showCategoryModal: false,
      isEditing: false,
      selectedCategory: '',
      newCategory: '',
      editingCriterion: { category: null, index: -1 },
      newCriteria: {
        title: '',
        key: '',
        desc: '',
        value: '10'
      },
      configTypes: {
        appraisal: { title: 'Appraisal' },
        fitment: { title: 'Fitment' },
        succession: { title: 'Succession' },
        performance: { title: 'Performance' }
      },
      // Default configurations
      configurations: {
        appraisal: {
          'GOALS': [
            { title: 'KPIs', value: '10', key: 'KP', desc: '(Reflect on the goals set and assess the extent to which they were achieved)' },
            { title: 'QUALITY', value: '10', key: 'QU', desc: '(Evaluate the quality and accuracy of your contribution, considering any feedback received)' },
            { title: 'IMPACT', value: '10', key: 'IM', desc: '(Reflect on how your work contributed to the overall goals and success measures)' },
          ],
          'KNOWLEDGE': [
            { title: 'STRENGTHS', value: '5', key: 'SR', desc: '(Reflect on key strengths and areas for improvement in context of the role.)' },
            { title: 'LEARNING', value: '5', key: 'LR', desc: '(Evaluate efforts to acquire skills or knowledge through training, courses, self-study.)' },
            { title: 'APPLICATION OF SKILLS', value: '5', key: 'AS', desc: '(Did you get opportunity to apply acquired skills or knowledge to your work)' },
          ],
          'BEHAVIOUR': [
            { title: 'COMMUNICATION', value: '5', key: 'CM', desc: '(Reflect on your ability to listen actively, and communicate appropriately in professional dialogue.)' },
            { title: 'COLLABORATION', value: '5', key: 'CL', desc: '(Assess your ability to maintain positive working relationships, and contribute to teamwork.)' },
            { title: 'SUPPORT', value: '5', key: 'SR', desc: '' },
          ],
          '': [
            { title: 'X-FACTOR', value: '10', key: 'XF', desc: '(Anything special beyond the scope of defined indicators. Scarce skills, Special Referral, etc.)' }
          ]
        },
        fitment: {
          'ABILITY': [
            { title: 'FUNCTIONAL READINESS', value: '10', key: 'FR', desc: 'Has functional skills as per the role' },
            { title: 'ROLE KNOWLEDGE', value: '10', key: 'RK', desc: 'Has relevant role knowledge to perform tasks' },
            { title: 'RELEVANT EXPERIENCE', value: '10', key: 'RE', desc: 'Has experience to manage optimum performance' },
          ],
          'ASPIRATION': [
            { title: 'INTEREST', value: '10', key: 'IR', desc: 'Interest (Do they want to move?)' },
            { title: 'MOBILITY', value: '10', key: 'MO', desc: 'Mobility (Can they move?)' },
            { title: 'SUPPORT', value: '10', key: 'SP', desc: 'Has support to manage optimal performance' },
          ],
          'ATTITUDE': [
            { title: 'BEHAVIOURAL ATTRIBUTES', value: '10', key: 'BA', desc: 'Has behavioural attributes matching the need of the role' },
            { title: 'COGNITIVE COMPLEXITY', value: '10', key: 'CC', desc: 'Has the cognitive complexity to understand the needS' },
            { title: 'LEARNING APTITUDE', value: '10', key: 'LA', desc: 'HaS learning aptitude to quickly learn role specific' },
          ],
          '': [
            { title: 'X-FACTOR', value: '10', key: 'XF', desc: '(Anything special beyond the scope of defined indicators. Scarce skills, Special Referral, etc.)' }
          ]
        },
        succession: {
          'AGILITY': [
            { title: 'TRANSITION ABILITY', value: '10', key: 'TA', desc: '' },
            { title: 'KNOWLEDGE READINESS', value: '10', key: 'KR', desc: '' },
            { title: 'RELEVANT EXPERIENCE', value: '10', key: 'RE', desc: '' },
          ],
          'ADAPTABILITY': [
            { title: 'EMOTIONAL INTELLIGENCE', value: '10', key: 'EI', desc: '' },
            { title: 'RESILIENCE', value: '10', key: 'RL', desc: '' },
            { title: 'LEARNING APTITUDE', value: '10', key: 'LA', desc: '' },
          ],
          '': [
            { title: 'X-FACTOR', value: '10', key: 'XF', desc: '(Anything special beyond the scope of defined indicators. Scarce skills, Special Referral, etc.)' }
          ]
        },
        performance: {
          'GOALS': [
            { title: 'KPIs', value: '10', key: 'KP', desc: '(Reflect on the goals set and assess the extent to which they were achieved)' },
            { title: 'QUALITY', value: '10', key: 'QU', desc: '(Evaluate the quality and accuracy of your contribution, considering any feedback received)' },
            { title: 'IMPACT', value: '10', key: 'IM', desc: '(Reflect on how your work contributed to the overall goals and success measures)' },
          ],
          'SKILL': [
            { title: 'IQ', value: '10', key: 'IQ', desc: 'Intelligence Quotient to operate as per the complexity of the role' },
            { title: 'TQ', value: '10', key: 'TQ', desc: 'Technical Quotient to have necessary skills to work on tasks' },
          ],
          '': [
            { title: 'X-FACTOR', value: '10', key: 'XF', desc: '(Anything special beyond the scope of defined indicators. Scarce skills, Special Referral, etc.)' }
          ]
        }
      }
    };
  },
  computed: {
    orgData() {
      return this.$store.state.organization.orgData;
    }
  },
  watch: {
    // Sync with existing DB settings if they exist
    orgData: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.settings && newVal.settings.evaluation_metrics) {
          // Deep copy to prevent mutating the vuex state directly
          this.configurations = JSON.parse(JSON.stringify(newVal.settings.evaluation_metrics));
        }
      }
    }
  },
  methods: {
    getCurrentConfig() {
      return this.configurations[this.selectedConfigType] || {};
    },

    // --- CATEGORY METHODS ---
    openCategoryModal() {
      this.newCategory = '';
      this.showCategoryModal = true;
    },
    closeCategoryModal() {
      this.showCategoryModal = false;
      this.newCategory = '';
    },
    addCategory() {
      if (this.newCategory.trim()) {
        const uppercaseCat = this.newCategory.toUpperCase();
        // Check if it already exists to avoid overwriting
        if (!this.configurations[this.selectedConfigType][uppercaseCat]) {
          this.$set(this.configurations[this.selectedConfigType], uppercaseCat, []);
        }
        this.closeCategoryModal();
      }
    },

    // --- CRITERIA METHODS ---
    openCriterionModal(categoryName) {
      this.isEditing = false;
      this.selectedCategory = categoryName;
      this.newCriteria = { title: '', key: '', desc: '', value: '10' };
      this.showCriteriaModal = true;
    },
    openEditCriterionModal(category, index) {
      this.isEditing = true;
      this.editingCriterion = { category, index };
      
      // Make a rock-solid safe copy so we don't accidentally edit the live state
      const targetItem = this.configurations[this.selectedConfigType][category][index];
      this.newCriteria = JSON.parse(JSON.stringify(targetItem)); 
      
      this.showCriteriaModal = true;
    },
    closeCriterionModal() {
      this.showCriteriaModal = false;
      this.isEditing = false;
      this.selectedCategory = '';
      this.editingCriterion = { category: null, index: -1 };
    },
    addCriterion() {
      if (this.newCriteria.title && this.newCriteria.key) {
        // Ensure category array exists before pushing
        if (!this.configurations[this.selectedConfigType][this.selectedCategory]) {
           this.$set(this.configurations[this.selectedConfigType], this.selectedCategory, []);
        }
        this.configurations[this.selectedConfigType][this.selectedCategory].push({ ...this.newCriteria });
        this.closeCriterionModal();
      }
    },
    updateCriterion() {
      if (this.newCriteria.title && this.newCriteria.key) {
        const { category, index } = this.editingCriterion;
        
        // This is the FIX for the error: Safe Vue 2 array mutation
        if (category && this.configurations[this.selectedConfigType][category]) {
          this.configurations[this.selectedConfigType][category].splice(index, 1, { ...this.newCriteria });
        }
        
        this.closeCriterionModal();
      }
    },
    removeCriterion(categoryName, index) {
      this.configurations[this.selectedConfigType][categoryName].splice(index, 1);
    },

    // --- SAVE TO DB ---
    async saveConfigurationToServer() {
      try {
        this.$vs.loading();
        const apiToken = localStorage.getItem('accessToken');
        const API_BASE_URL = "https://api-core-gateway.getboarded.com";

        const response = await fetch(`${API_BASE_URL}/organization/${this.orgData._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiToken}`
          },
          body: JSON.stringify({
            settings: {
              evaluation_metrics: this.configurations
            }
          })
        });

        if (!response.ok) {
           throw new Error("Backend failed to save settings");
        }

        this.$vs.notify({
          title: 'Success',
          text: 'AI Models updated with your custom metrics!',
          color: 'success'
        });
      } catch (error) {
        console.error("Save Error:", error);
        this.$vs.notify({
          title: 'Error',
          text: 'Unable to save metrics. Please try again.',
          color: 'danger'
        });
      } finally {
        this.$vs.loading.close();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.criterion-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;

  &:hover {
    border-color: #e5e7eb;
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
}

.popup-card {
  >>>.vs-popup--header {
    display: none !important; /* Keep default header hidden */
  }

  >>>.vs-popup {
    width: 500px !important;
  }
}

.con-vs-popup>>>.vs-popup--content {
  padding: 0 !important;
  margin: 0 !important;
  overflow-x: hidden !important;
  width: auto !important;
  border-radius: 8px !important;
  background: white !important;
}

.con-vs-popup>>>.vs-popup {
  background: white !important;
  border-radius: 8px !important;
}

.vs-input>>>.vs-input--input {
  box-shadow: none;
}

.vs-input>>>.vs-input--placeholder {
  top: 5px;
}

.custom-category-btn {
  border: 1px solid #f5bb00 !important;
  color: #f5bb00 !important;
  background: transparent !important;
  border-radius: 999px !important;

  padding: 6px 16px !important;
  font-size: 14px !important;
  font-weight: 400 !important;

  box-shadow: none !important;
  transition: all 0.15s ease;
}

.custom-category-btn:hover {
  background: #fff7cc !important;
}

.custom-category-btn .material-icons {
  font-size: 16px !important;
}
</style>